import {faAt, faChevronDown, faPhone} from '@fortawesome/pro-regular-svg-icons';
import {faEllipsisVertical} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Dropdown, Flex, Space, Tooltip} from 'antd';
import DOMPurify from 'dompurify';
import {ReactElement, useCallback, useMemo, useState} from 'react';

import {Text} from '../../../../../../../component/text/text';
import {spacer_s} from '../../../../../../../css/var-export.scss';
import {RatingStarLine} from '../../../../../../../layout/rating-star-line/rating-star-line';
import {Locale} from '../../../../../../../provider/locale/localization';
import {useUser} from '../../../../../../../provider/user/user-hook';
import {fetchReviewSentimentUpdate} from '../../../../../../../service/reviews/reviews';
import {ReviewInfoDataType} from '../../../../../../../service/reviews/reviews-info';
import {NoteFormType, useNotes, useNotesMutation} from '../../../../../../../service/reviews/reviews-notes';
import {useReviewSentiments} from '../../../../../../../service/reviews/reviews-tags';
import {
    ReviewSentimentEnum,
    ReviewSentimentOptionType,
    ReviewSentimentType,
} from '../../../../../../../service/reviews/reviews-type';
import {useCopy} from '../../../../../../../util/copy-hook/copy-hook';
import {useFormat} from '../../../../../../../util/format-hook/format-hook';
import {getHtmlTextContent} from '../../../../../../../util/string';
import {ReviewStatus} from '../../../../review-status/review-status';
import {getReviewCreationDate} from '../../../../reviews-helper';
import {useReviewStateHook} from '../../../../reviews-state/reviews-state-provider';
import {ReviewDataWithCatalogType} from '../../review-list-type';
import {useReviewMenu} from '../review-menu-hook/review-menu-hook';

import {ReviewHistoryModal} from './history-modal/history-modal';
import {NoteModal} from './note-modal/note-modal';
import {ResponsibleUserModal} from './responsible-user-modal/responsible-user-modal';
import {StatusTag} from './status-tag/status-tag';
import * as styles from './review-header.scss';

type PropsType = {
    reviewData: ReviewDataWithCatalogType;
    isReviewDeleted: boolean;
    info: ReviewInfoDataType | null;
    isFirstReview: boolean;
    isTourEligible?: boolean;
};

// eslint-disable-next-line complexity, max-statements
export function ReviewHeader(props: PropsType): ReactElement {
    const {reviewData, isReviewDeleted, info, isFirstReview, isTourEligible} = props;

    const {
        id,
        author,
        catalog,
        ableToChangeResponsibleUser,
        originUrl,
        rating,
        comment,
        createdInCatalog,
        processStatus,
        userFields,
        responsibleUser,
        replyTimeInRd,
        replyTimeInCatalog,
        createdInRd,
        deletedAt,
        isChanged,
        sentiment,
        catalogId,
    } = reviewData;

    const [isOpenNoteModal, setIsOpenNoteModal] = useState<boolean>(false);
    const [isOpenHistoryModal, setIsOpenHistoryModal] = useState<boolean>(false);
    const [isOpenResponsibleUserModal, setIsOpenResponsibleUserModal] = useState<boolean>(false);
    const {user} = useUser();
    const {getFormattedDateTime} = useFormat();
    const onCopy = useCopy('TEXT__COPIED');

    const {refreshReview, refreshReviewsInfo, refreshCatalogs, updateReview} = useReviewStateHook();
    const {mutateAsync, isLoading} = useNotesMutation();

    const {refetch: refreshNotes} = useNotes(id, Number(info?.notesCount) > 0 || false);

    const {data: sentimentsMap} = useReviewSentiments();
    const [isSentimentUpdating, setIsSentimentUpdating] = useState<boolean>(false);
    const sanitizedAuthor = useMemo(() => DOMPurify.sanitize(author || ''), [author]);
    const sanitizedPhone = useMemo(() => DOMPurify.sanitize(userFields.phone || ''), [userFields.phone]);
    const sanitizedEmail = useMemo(() => DOMPurify.sanitize(userFields.email || ''), [userFields.email]);
    const plainPhoneForCopy = useMemo(() => getHtmlTextContent(sanitizedPhone), [sanitizedPhone]);
    const plainEmailForCopy = useMemo(() => getHtmlTextContent(sanitizedEmail), [sanitizedEmail]);

    async function refreshNotesFromModal() {
        await refreshNotes();
    }

    const timeZoneObject = user?.profile?.timezone ? {timeZone: user?.profile?.timezone} : {};

    async function onSubmit(formValues: NoteFormType) {
        return mutateAsync(
            {text: (formValues.text ?? '').trim(), reviewId: id},
            {
                onSuccess: async () => {
                    if (!(Number(info?.notesCount) > 0)) {
                        await refreshReviewsInfo();
                    }

                    setIsOpenNoteModal(false);
                    await refreshNotesFromModal();
                },
            }
        );
    }

    const handleSubmitFinishReviewForm = useCallback(() => {
        if (refreshReview) {
            refreshReview(id);
        }

        if (refreshCatalogs) {
            refreshCatalogs();
        }
    }, [id, refreshCatalogs, refreshReview]);

    const isShowExternalOptions = Boolean(catalog?.is_external_reviews) && !deletedAt;

    const menuItems = useReviewMenu({
        catalogId,
        reviewId: id,
        isShowExternalOptions,
        text: comment || '',
        setIsOpenNoteModal,
        setIsOpenHistoryModal,
        setIsOpenResponsibleUserModal,
        ableToChangeResponsibleUser,
        onSubmitFinish: handleSubmitFinishReviewForm,
    });

    const sentimentItems = useMemo<Array<ReviewSentimentOptionType>>(
        () =>
            sentimentsMap
                ? [
                      {
                          key: ReviewSentimentEnum.positive,
                          label: sentimentsMap.positive,
                      },
                      {
                          key: ReviewSentimentEnum.neutral,
                          label: sentimentsMap.neutral,
                      },
                      {
                          key: ReviewSentimentEnum.negative,
                          label: sentimentsMap.negative,
                      },
                  ]
                : [],
        [sentimentsMap]
    );

    const currentSentimentLabel = useMemo(() => {
        if (!sentimentsMap || !sentiment) {
            return null;
        }

        return sentimentsMap[sentiment];
    }, [sentimentsMap, sentiment]);

    const handleSentimentChange = useCallback(
        async (newSentiment: ReviewSentimentType) => {
            if (sentiment === newSentiment || isSentimentUpdating) {
                return;
            }

            try {
                setIsSentimentUpdating(true);
                await fetchReviewSentimentUpdate(id, {sentiment: newSentiment});
                updateReview(id, {sentiment: newSentiment as ReviewSentimentEnum});
            } finally {
                setIsSentimentUpdating(false);
            }
        },
        [id, isSentimentUpdating, sentiment, updateReview]
    );

    return (
        <div>
            <Flex className={styles.ReviewHeader_infoWrapper}>
                <Flex gap={spacer_s} vertical>
                    <Space size={12} wrap>
                        {sanitizedAuthor && (
                            <Text bolder large>
                                <span
                                    className={styles.ReviewHeader_author}
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{__html: sanitizedAuthor}}
                                />
                            </Text>
                        )}
                        <Tooltip
                            placement="bottomLeft"
                            rootClassName={styles.ReviewHeader_parseDateTooltip}
                            title={
                                <Flex vertical>
                                    <Flex className={styles.ReviewHeader_parseDate}>
                                        <Locale
                                            stringKey="REVIEWS__REVIEWS_PARSED_AT"
                                            valueMap={{
                                                date: (
                                                    <span>
                                                        {getFormattedDateTime(new Date(createdInRd), {
                                                            year: 'numeric',
                                                            month: '2-digit',
                                                            day: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            ...timeZoneObject,
                                                        })}
                                                    </span>
                                                ),
                                            }}
                                        />
                                    </Flex>

                                    {createdInCatalog && (
                                        <Flex className={styles.ReviewHeader_parseDate}>
                                            <Locale
                                                stringKey="REVIEWS__REVIEWS_IN_CATALOG"
                                                valueMap={{
                                                    date: getFormattedDateTime(new Date(createdInCatalog), {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        ...timeZoneObject,
                                                    }),
                                                }}
                                            />
                                        </Flex>
                                    )}
                                </Flex>
                            }
                        >
                            <Text secondary>
                                {getFormattedDateTime(new Date(getReviewCreationDate(reviewData)), {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    ...timeZoneObject,
                                })}
                            </Text>
                        </Tooltip>
                    </Space>
                    <Space align="center" size={12} wrap>
                        {typeof rating === 'number' && <RatingStarLine rating={rating} />}
                        {sentimentItems.length > 0 && !isReviewDeleted && sentiment !== null && (
                            <Dropdown
                                disabled={isSentimentUpdating}
                                menu={{
                                    items: sentimentItems,
                                    onClick: ({key}) => handleSentimentChange(key as ReviewSentimentType),
                                }}
                                placement="bottomLeft"
                                trigger={['click']}
                            >
                                <Button className={styles.ReviewHeader_sentimentSelect} size="small" type="text">
                                    <Space size={4}>
                                        <Text>{currentSentimentLabel}</Text>
                                        <FontAwesomeIcon icon={faChevronDown} />
                                    </Space>
                                </Button>
                            </Dropdown>
                        )}
                    </Space>
                    <Space>
                        <StatusTag
                            isChanged={isChanged}
                            isDeleted={isReviewDeleted}
                            openHistoryModalHandler={() => setIsOpenHistoryModal(true)}
                        />
                    </Space>
                </Flex>

                <div className={styles.ReviewHeader_statusWrapper}>
                    <ReviewStatus
                        className={styles.ReviewHeader_status}
                        isFirstReview={isFirstReview}
                        isTourEligible={isTourEligible}
                        processStatus={processStatus}
                        reviewId={id}
                        updateReview={updateReview}
                    />
                    <Dropdown autoAdjustOverflow menu={{items: menuItems}} placement="bottomRight" trigger={['click']}>
                        <Button className={styles.ReviewHeader_dropdownButton} type="text">
                            <FontAwesomeIcon icon={faEllipsisVertical} />
                        </Button>
                    </Dropdown>
                </div>
            </Flex>

            {(userFields.phone || userFields.email) && (
                <Space wrap>
                    {userFields?.phone && (
                        <Space
                            className={styles.ReviewHeader_userEmail}
                            onClick={() => onCopy(plainPhoneForCopy)}
                            size={4}
                        >
                            <FontAwesomeIcon icon={faPhone} />
                            <Text inheritColor>
                                <span
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{__html: sanitizedPhone}}
                                />
                            </Text>
                        </Space>
                    )}
                    {userFields?.email && (
                        <Space
                            className={styles.ReviewHeader_userEmail}
                            onClick={() => onCopy(plainEmailForCopy)}
                            size={4}
                        >
                            <FontAwesomeIcon icon={faAt} />
                            <Text inheritColor>
                                <span
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{__html: sanitizedEmail}}
                                />
                            </Text>
                        </Space>
                    )}
                </Space>
            )}

            {isOpenNoteModal && (
                <NoteModal
                    isLoading={isLoading}
                    onCancel={setIsOpenNoteModal}
                    onSubmit={onSubmit}
                    reviewId={id}
                    title={<Locale stringKey="REVIEWS__NOTE_MODAL__TITLE" />}
                />
            )}
            {isOpenHistoryModal && (
                <ReviewHistoryModal
                    onCancel={() => setIsOpenHistoryModal(false)}
                    replyTimeInCatalog={replyTimeInCatalog}
                    replyTimeInRd={replyTimeInRd}
                    reviewAuthor={originUrl ? null : author}
                    reviewCreatedInCatalog={createdInCatalog}
                    reviewId={id}
                />
            )}

            {isOpenResponsibleUserModal && (
                <ResponsibleUserModal
                    initialSelectedUserId={responsibleUser?.id}
                    onCancel={() => setIsOpenResponsibleUserModal(false)}
                    reviewData={reviewData}
                    reviewId={id}
                    updateReview={updateReview}
                />
            )}
        </div>
    );
}
